import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  List as ListIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  QuestionMark,
  AudioFile,
  TextFields,
  RadioButtonChecked,
} from '@mui/icons-material';
import {
  CUSTOM_QUESTION_TYPE_VALUE,
  KNOWN_QUESTION_TYPES,
  getQuestionTypeSelection,
  isValidCustomQuestionTypeSlug,
  resolveQuestionType,
} from '../constants/questionTypes';

interface Question {
  id: string;
  text: string;
  question_type: string;
  section_id?: string;
  sub_section_id?: string;
  section_name: string;
  subsection_name: string;
  difficulty_level: string;
  max_score: number;
  score: number;
  audio_url?: string;
  paragraph_text?: string;
  options?: string[];
  correct_answer?: string;
  created_at: string;
  // New properties for enhanced content structure
  parent_question_id?: string;
  parent_question_text?: string;
  section?: { id: string; name: string };
  subsection?: { id: string; name: string };
  content?: {
    question_type?: string;
    title?: string;
    difficulty_level?: string;
    audio_url?: string;
    paragraph?: string;
    instructions?: string;
    duration_minutes?: number;
    total_child_questions?: number;
    question_number?: number;
    depends_on_parent?: boolean;
    options?: string[];
    correct_answer?: string;
    time_limit_seconds?: number;
    evaluation_criteria?: string[];
  };
}

const emptyFormData = {
  text: '',
  question_type: '',
  custom_question_type: '',
  section_id: '',
  subsection_id: '',
  parent_question_id: '',
  difficulty_level: '',
  max_score: 10,
  duration_minutes: 5,
  audio_url: '',
  paragraph: '',
  paragraph_text: '',
  instructions: '',
  total_child_questions: 1,
  question_number: 1,
  depends_on_parent: false,
  options: ['', '', '', ''] as string[],
  correct_answer: '',
};

const QuestionsSimple: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  
  // Form states
  const [formData, setFormData] = useState(emptyFormData);

  const effectiveQuestionType = resolveQuestionType(
    formData.question_type,
    formData.custom_question_type
  );

  const isCustomTypeSelected = formData.question_type === CUSTOM_QUESTION_TYPE_VALUE;
  const customTypeValid =
    !isCustomTypeSelected || isValidCustomQuestionTypeSlug(formData.custom_question_type);
  
  // Sections and subsections for dropdowns
  const [sections, setSections] = useState<any[]>([]);
  const [subsections, setSubsections] = useState<any[]>([]);
  const [parentQuestions, setParentQuestions] = useState<any[]>([]);

  // Load initial data
  useEffect(() => {
    loadQuestions();
    loadSections();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        API_ENDPOINTS.QUESTIONS_WITH_FILTERS({ skip: 0, limit: 20 })
      );
      const result = await response.json();

      if (result.status === 'success') {
        setQuestions(result.data || []);
      } else {
        setError(result.message || 'Failed to load questions');
      }
    } catch (err) {
      setError('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async () => {
    try {
      // Load sections from API
      const sectionsResponse = await fetch(API_ENDPOINTS.SECTIONS);
      const sectionsData = await sectionsResponse.json();
      
      if (sectionsData.status === 'success') {
        setSections(sectionsData.data);
      }

      // Load all subsections
      const subsectionsResponse = await fetch(API_ENDPOINTS.SUBSECTIONS);
      const subsectionsData = await subsectionsResponse.json();
      
      if (subsectionsData.status === 'success') {
        setSubsections(subsectionsData.data);
      }
      
      // Load parent questions
      const parentQuestionsResponse = await fetch(API_ENDPOINTS.PARENT_QUESTIONS);
      const parentQuestionsData = await parentQuestionsResponse.json();
      
      if (parentQuestionsData.status === 'success') {
        setParentQuestions(parentQuestionsData.data);
      }
    } catch (err) {
      console.error('Failed to load sections:', err);
      setError('Failed to load sections and subsections');
    }
  };

  const handleAddQuestion = async () => {
    try {
      if (!effectiveQuestionType) {
        setError('Please select or enter a question type');
        return;
      }
      if (isCustomTypeSelected && !customTypeValid) {
        setError('Custom type must be snake_case (e.g. opinion_reasoning)');
        return;
      }

      setLoading(true);
      
      // Prepare comprehensive question data with content structure
      const content = {
        question_type: effectiveQuestionType,
        difficulty_level: formData.difficulty_level,
        audio_url: formData.audio_url || null,
        paragraph: formData.paragraph || formData.paragraph_text || null,
        instructions: formData.instructions || null,
        duration_minutes: formData.duration_minutes || null,
        total_child_questions: formData.total_child_questions || null,
        question_number: formData.question_number || null,
        depends_on_parent: formData.depends_on_parent || false,
        options: effectiveQuestionType === 'multiple_choice' ? formData.options : null,
        correct_answer: effectiveQuestionType === 'multiple_choice' ? formData.correct_answer : null,
      };
      
      // Remove null values from content
      const cleanContent = Object.fromEntries(
        Object.entries(content).filter(([_, value]) => value !== null && value !== '')
      );
      
      const questionData = {
        text: formData.text,
        section_id: formData.section_id,
        subsection_id: formData.subsection_id,
        parent_question_id: formData.parent_question_id || null,
        max_score: formData.max_score,
        duration_minutes: formData.duration_minutes,
        content: cleanContent,
        // Also send individual fields for backward compatibility
        question_type: effectiveQuestionType,
        difficulty_level: formData.difficulty_level,
        audio_url: formData.audio_url || null,
        paragraph: formData.paragraph || formData.paragraph_text || null,
        paragraph_text: formData.paragraph || formData.paragraph_text || null,
        instructions: formData.instructions || null,
        total_child_questions: formData.total_child_questions || null,
        question_number: formData.question_number || null,
        depends_on_parent: formData.depends_on_parent || false,
        options: effectiveQuestionType === 'multiple_choice' ? formData.options : null,
        correct_answer: effectiveQuestionType === 'multiple_choice' ? formData.correct_answer : null,
      };
      
      // Call API to create question
      const response = await fetch(API_ENDPOINTS.CREATE_QUESTION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(questionData),
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setSuccess('Question added successfully!');
        setAddDialogOpen(false);
        resetForm();
        loadQuestions();
        loadSections();
      } else {
        setError(result.message || 'Failed to add question');
      }
    } catch (err) {
      console.error('Error adding question:', err);
      setError('Failed to add question');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (question: Question) => {
    const content = question.content || {};
    const options = content.options || question.options || ['', '', '', ''];
    const paddedOptions = [...options];
    while (paddedOptions.length < 4) {
      paddedOptions.push('');
    }

    setEditingQuestion(question);
    const existingType = content.question_type || question.question_type || '';
    const typeSelection = getQuestionTypeSelection(existingType);
    setFormData({
      text: question.text || '',
      question_type: typeSelection,
      custom_question_type:
        typeSelection === CUSTOM_QUESTION_TYPE_VALUE ? existingType : '',
      section_id: question.section_id || question.section?.id || '',
      subsection_id: question.sub_section_id || question.subsection?.id || '',
      parent_question_id: question.parent_question_id || '',
      difficulty_level: content.difficulty_level || question.difficulty_level || '',
      max_score: question.score || question.max_score || 10,
      duration_minutes: content.duration_minutes || 5,
      audio_url: content.audio_url || question.audio_url || '',
      paragraph: content.paragraph || question.paragraph_text || '',
      paragraph_text: content.paragraph || question.paragraph_text || '',
      instructions: content.instructions || '',
      total_child_questions: content.total_child_questions || 1,
      question_number: content.question_number || 1,
      depends_on_parent: content.depends_on_parent || false,
      options: paddedOptions.slice(0, 4),
      correct_answer: content.correct_answer || question.correct_answer || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion) return;

    try {
      if (!effectiveQuestionType) {
        setError('Please select or enter a question type');
        return;
      }
      if (isCustomTypeSelected && !customTypeValid) {
        setError('Custom type must be snake_case (e.g. opinion_reasoning)');
        return;
      }

      setLoading(true);

      const content = {
        question_type: effectiveQuestionType,
        difficulty_level: formData.difficulty_level,
        audio_url: formData.audio_url || null,
        paragraph: formData.paragraph || formData.paragraph_text || null,
        instructions: formData.instructions || null,
        duration_minutes: formData.duration_minutes || null,
        total_child_questions: formData.total_child_questions || null,
        question_number: formData.question_number || null,
        depends_on_parent: formData.depends_on_parent || false,
        options: effectiveQuestionType === 'multiple_choice' ? formData.options : null,
        correct_answer: effectiveQuestionType === 'multiple_choice' ? formData.correct_answer : null,
      };

      const cleanContent = Object.fromEntries(
        Object.entries(content).filter(([_, value]) => value !== null && value !== '')
      );

      const questionData = {
        text: formData.text,
        section_id: formData.section_id,
        subsection_id: formData.subsection_id,
        parent_question_id: formData.parent_question_id || null,
        max_score: formData.max_score,
        duration_minutes: formData.duration_minutes,
        content: cleanContent,
        question_type: effectiveQuestionType,
        difficulty_level: formData.difficulty_level,
        audio_url: formData.audio_url || null,
        paragraph: formData.paragraph || formData.paragraph_text || null,
        instructions: formData.instructions || null,
        total_child_questions: formData.total_child_questions || null,
        question_number: formData.question_number || null,
        depends_on_parent: formData.depends_on_parent || false,
        options: effectiveQuestionType === 'multiple_choice' ? formData.options : null,
        correct_answer: effectiveQuestionType === 'multiple_choice' ? formData.correct_answer : null,
      };

      const response = await fetch(API_ENDPOINTS.UPDATE_QUESTION(editingQuestion.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(questionData),
      });

      const result = await response.json();

      if (result.status === 'success') {
        setSuccess('Question updated successfully!');
        setEditDialogOpen(false);
        setEditingQuestion(null);
        resetForm();
        loadQuestions();
        loadSections();
      } else {
        setError(result.message || result.detail || 'Failed to update question');
      }
    } catch (err) {
      console.error('Error updating question:', err);
      setError('Failed to update question');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (question: Question) => {
    const isParent = !question.parent_question_id;
    const confirmMessage = isParent
      ? 'Are you sure you want to delete this question? If it has child questions, they will also be soft-deleted.'
      : 'Are you sure you want to delete this question?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.DELETE_QUESTION(question.id), {
        method: 'DELETE',
      });
      const result = await response.json();

      if (result.status === 'success') {
        const childrenCount = result.data?.deleted_children_count || 0;
        setSuccess(
          childrenCount > 0
            ? `Question deleted successfully (${childrenCount} child question(s) also deleted).`
            : 'Question deleted successfully!'
        );
        loadQuestions();
        loadSections();
      } else {
        setError(result.message || result.detail || 'Failed to delete question');
      }
    } catch (err) {
      console.error('Error deleting question:', err);
      setError('Failed to delete question');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyFormData);
  };

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'multiple_choice':
        return <RadioButtonChecked color="primary" />;
      case 'text_input':
        return <TextFields color="success" />;
      case 'audio_comprehension':
        return <AudioFile color="warning" />;
      default:
        return <QuestionMark />;
    }
  };

  const getQuestionTypeColor = (type: string) => {
    switch (type) {
      case 'multiple_choice':
        return 'primary';
      case 'text_input':
        return 'success';
      case 'audio_comprehension':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'success';
      case 'intermediate':
        return 'warning';
      case 'advanced':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 4, minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" fontWeight={800} className="gradient-text" gutterBottom>
          📝 Questions Management
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Create, manage, and organize assessment questions
        </Typography>
      </Box>

      {/* Action Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card 
          sx={{ 
            flex: 1,
            minWidth: 300,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}
          onClick={() => setAddDialogOpen(true)}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <AddIcon sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Add New Question
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Create questions for assessments with various types and configurations
            </Typography>
          </CardContent>
        </Card>

        <Card 
          sx={{ 
            flex: 1,
            minWidth: 300,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}
          onClick={() => navigate('/view-questions')}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <ListIcon sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              View All Questions
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Browse, edit, and manage existing questions in the database
            </Typography>
          </CardContent>
        </Card>

        <Card 
          sx={{ 
            flex: 1,
            minWidth: 300,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}
          onClick={() => navigate('/sections-management')}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>            
            <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              📁 Manage Sections
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Create, edit, and organize sections and subsections for questions
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Questions Table */}
      <Card sx={{ borderRadius: '16px' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600}>
              📋 Questions Database ({questions.length} questions)
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading questions...
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Question</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Section</strong></TableCell>
                    <TableCell><strong>Difficulty</strong></TableCell>
                    <TableCell><strong>Score</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questions.map((question) => (
                    <TableRow key={question.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {question.text.length > 60 
                            ? `${question.text.substring(0, 60)}...` 
                            : question.text
                          }
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {question.subsection_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getQuestionTypeIcon(question.content?.question_type || question.question_type || 'unknown')}
                          label={(question.content?.question_type || question.question_type || 'unknown').replace(/_/g, ' ')}
                          color={getQuestionTypeColor(question.content?.question_type || question.question_type || 'unknown') as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {question.section_name || question.section?.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={question.content?.difficulty_level || question.difficulty_level || 'intermediate'}
                          color={getDifficultyColor(question.content?.difficulty_level || question.difficulty_level || 'intermediate') as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {question.score || question.max_score} pts
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedQuestion(question);
                              setViewDialogOpen(true);
                            }}
                            title="View Details"
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEditDialog(question)}
                            title="Edit Question"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(question)}
                            title="Delete Question"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Question Dialog */}
      <Dialog 
        open={addDialogOpen || editDialogOpen} 
        onClose={() => {
          setAddDialogOpen(false);
          setEditDialogOpen(false);
          setEditingQuestion(null);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            {editDialogOpen ? 'Edit Question' : '➕ Add New Question'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Question Text"
              multiline
              rows={3}
              value={formData.text}
              onChange={(e) => setFormData({ ...formData, text: e.target.value })}
              placeholder="Enter the question text..."
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Question Type</InputLabel>
                <Select
                  value={formData.question_type}
                  label="Question Type"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      question_type: e.target.value,
                      custom_question_type:
                        e.target.value === CUSTOM_QUESTION_TYPE_VALUE
                          ? formData.custom_question_type
                          : '',
                    })
                  }
                >
                  {KNOWN_QUESTION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                  <MenuItem value={CUSTOM_QUESTION_TYPE_VALUE}>Custom / Other</MenuItem>
                </Select>
              </FormControl>

              {isCustomTypeSelected && (
                <TextField
                  label="Custom Type Slug"
                  value={formData.custom_question_type}
                  onChange={(e) =>
                    setFormData({ ...formData, custom_question_type: e.target.value })
                  }
                  placeholder="e.g. opinion_reasoning"
                  helperText={
                    formData.custom_question_type && !customTypeValid
                      ? 'Use snake_case: lowercase letters, numbers, underscores'
                      : 'Must match assessment question_selection key'
                  }
                  error={Boolean(formData.custom_question_type) && !customTypeValid}
                  sx={{ minWidth: 240 }}
                />
              )}

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Section</InputLabel>
                <Select
                  value={formData.section_id}
                  onChange={(e) => setFormData({ ...formData, section_id: e.target.value, subsection_id: '' })}
                >
                  {sections.map((section) => (
                    <MenuItem key={section.id} value={section.id}>
                      {section.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Subsection</InputLabel>
                <Select
                  value={formData.subsection_id}
                  onChange={(e) => setFormData({ ...formData, subsection_id: e.target.value })}
                  disabled={!formData.section_id}
                >
                  {subsections
                    .filter(sub => sub.section_id === formData.section_id)
                    .map((subsection) => (
                      <MenuItem key={subsection.id} value={subsection.id}>
                        {subsection.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Parent Question (Optional)</InputLabel>
                <Select
                  value={formData.parent_question_id}
                  onChange={(e) => setFormData({ ...formData, parent_question_id: e.target.value })}
                  disabled={!formData.section_id}
                >
                  <MenuItem value="">None (Standalone Question)</MenuItem>
                  {parentQuestions
                    .filter(parent => parent.section_id === formData.section_id && 
                                    (!formData.subsection_id || parent.sub_section_id === formData.subsection_id))
                    .map((parentQuestion) => (
                      <MenuItem key={parentQuestion.id} value={parentQuestion.id}>
                        {parentQuestion.text.length > 50 
                          ? `${parentQuestion.text.substring(0, 50)}...` 
                          : parentQuestion.text}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Difficulty Level</InputLabel>
                <Select
                  value={formData.difficulty_level}
                  onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Max Score"
                type="number"
                value={formData.max_score}
                onChange={(e) => setFormData({ ...formData, max_score: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 100 }}
                sx={{ minWidth: 120 }}
              />
              
              <TextField
                label="Duration (minutes)"
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 60 }}
                sx={{ minWidth: 120 }}
              />
            </Box>

            {/* Conditional Fields Based on Question Type */}
            
            {/* Instructions field for parent questions */}
            {(effectiveQuestionType === 'listening_comprehension_set' || 
              effectiveQuestionType === 'reading_comprehension_set' || 
              !formData.parent_question_id) && (
              <TextField
                fullWidth
                label="Instructions (Optional)"
                multiline
                rows={2}
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                placeholder="e.g., Listen to this audio paragraph carefully. You will hear it ONCE only, then answer questions."
              />
            )}
            
            {/* Fields for comprehension sets (parent questions) */}
            {(effectiveQuestionType === 'listening_comprehension_set' || 
              effectiveQuestionType === 'reading_comprehension_set') && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Total Child Questions"
                  type="number"
                  value={formData.total_child_questions}
                  onChange={(e) => setFormData({ ...formData, total_child_questions: parseInt(e.target.value) })}
                  inputProps={{ min: 1, max: 10 }}
                  sx={{ minWidth: 150 }}
                />
              </Box>
            )}
            
            {/* Audio URL for listening comprehension */}
            {(effectiveQuestionType === 'audio_comprehension' || 
              effectiveQuestionType === 'listening_comprehension_set') && (
              <TextField
                fullWidth
                label="Audio URL"
                value={formData.audio_url}
                onChange={(e) => setFormData({ ...formData, audio_url: e.target.value })}
                placeholder="https://gappeo.blob.core.windows.net/dev-gappeoai/language_assignments/english_assests/audio.mp3"
              />
            )}
            
            {/* Paragraph for reading comprehension */}
            {(effectiveQuestionType === 'reading_comprehension' || 
              effectiveQuestionType === 'reading_comprehension_set' ||
              effectiveQuestionType === 'listening_comprehension_set') && (
              <TextField
                fullWidth
                label="Paragraph/Content"
                multiline
                rows={4}
                value={formData.paragraph}
                onChange={(e) => setFormData({ ...formData, paragraph: e.target.value })}
                placeholder="Enter the reading passage or listening content..."
              />
            )}
            
            {/* Fields for child questions */}
            {formData.parent_question_id && (
              <Box>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Child Question Settings
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <TextField
                    label="Question Number"
                    type="number"
                    value={formData.question_number}
                    onChange={(e) => setFormData({ ...formData, question_number: parseInt(e.target.value) })}
                    inputProps={{ min: 1, max: 20 }}
                    sx={{ minWidth: 120 }}
                  />
                  <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Depends on Parent</InputLabel>
                    <Select
                      value={formData.depends_on_parent ? 'true' : 'false'}
                      onChange={(e) => setFormData({ ...formData, depends_on_parent: e.target.value === 'true' })}
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}
            
            {/* Multiple choice options */}
            {effectiveQuestionType === 'multiple_choice' && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Answer Options
                </Typography>
                {formData.options.map((option, index) => (
                  <TextField
                    key={index}
                    fullWidth
                    label={`Option ${String.fromCharCode(65 + index)}) ${option.replace(/^[A-D]\)\s*/, '')}`}
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...formData.options];
                      newOptions[index] = e.target.value;
                      setFormData({ ...formData, options: newOptions });
                    }}
                    placeholder={`${String.fromCharCode(65 + index)}) Enter option ${index + 1}`}
                    sx={{ mb: 2 }}
                  />
                ))}
                <TextField
                  fullWidth
                  label="Correct Answer"
                  value={formData.correct_answer}
                  onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                  placeholder="Enter the correct answer (e.g., A, B, C, or D)"
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddDialogOpen(false);
              setEditDialogOpen(false);
              setEditingQuestion(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={editDialogOpen ? handleUpdateQuestion : handleAddQuestion}
            disabled={
              loading ||
              !formData.text ||
              !effectiveQuestionType ||
              !customTypeValid
            }
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : editDialogOpen ? (
              'Save Changes'
            ) : (
              'Add Question'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Question Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            👁️ Question Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedQuestion && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedQuestion.text}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Type: <strong>{(selectedQuestion.content?.question_type || selectedQuestion.question_type || 'unknown').replace(/_/g, ' ')}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Section: <strong>{selectedQuestion.section_name || selectedQuestion.section?.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Subsection: <strong>{selectedQuestion.subsection_name || selectedQuestion.subsection?.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Difficulty: <strong>{selectedQuestion.content?.difficulty_level || selectedQuestion.difficulty_level || 'intermediate'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Max Score: <strong>{selectedQuestion.score || selectedQuestion.max_score} points</strong>
                </Typography>
              </Box>

              {selectedQuestion.options && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Answer Options:
                  </Typography>
                  {selectedQuestion.options.map((option, index) => (
                    <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                      {index + 1}. {option}
                    </Typography>
                  ))}
                  {selectedQuestion.correct_answer && (
                    <Typography variant="body2" color="success.main" sx={{ mt: 1, ml: 2 }}>
                      ✓ Correct Answer: {selectedQuestion.correct_answer}
                    </Typography>
                  )}
                </Box>
              )}

              {selectedQuestion.audio_url && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Audio URL:
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    {selectedQuestion.audio_url}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => setAddDialogOpen(true)}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default QuestionsSimple;
